import {
    faArrowUpRightFromSquare,
    faXmark,
} from "@fortawesome/pro-light-svg-icons";
import {
    faCircleExclamation,
    faCircleMinus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Typography from "@vapor/react-extended/ExtendedTypography";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Stack,
    useTheme,
} from "@vapor/react-material";
import dayjs from "dayjs";
import type { ReactElement } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
    ExpiringDelegated,
    ExpiringIntermediary,
    ExpiringSecurityEnvironment,
} from "../../../../../core/api/use-get-expiring-identities/ExpiringIdentitiesDTO";

export type Variant =
    | "expiringCredentials"
    | "expiredCredentials"
    | "expiringSecurityEnvironments"
    | "expiredSecurityEnvironments";

const DIALOG_TEXT: Record<Variant, string> = {
    expiringCredentials:
        "views.home.components.tax_submissions_table_header.expiringIdentities.dialog.credentials.expiring",
    expiredCredentials:
        "views.home.components.tax_submissions_table_header.expiringIdentities.dialog.credentials.expired",
    expiringSecurityEnvironments:
        "views.home.components.tax_submissions_table_header.expiringIdentities.dialog.securityEnvironment.expiring",
    expiredSecurityEnvironments:
        "views.home.components.tax_submissions_table_header.expiringIdentities.dialog.securityEnvironment.expired",
};

const getIdentityText = (
    identity:
        | ExpiringIntermediary
        | ExpiringDelegated
        | ExpiringSecurityEnvironment,
) => {
    if ("taxId" in identity) {
        return `${identity.taxId} - ${dayjs(identity.expirationDate).format(
            "DD/MM/YYYY",
        )}`;
    }
    return `${identity.intermediaryTaxId} - ${dayjs(
        identity.expirationDate,
    ).format("DD/MM/YYYY")}`;
};

export interface WarningNotificationContentProps {
    children: ReactElement;
    variant: Variant;
    identities:
        | ExpiringIntermediary[]
        | ExpiringDelegated[]
        | ExpiringSecurityEnvironment[];
    onDismiss?: () => void;
}

export const WarningNotificationContent: React.FC<
    WarningNotificationContentProps
> = ({ variant, children, identities, onDismiss }): ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [open, setOpen] = useState(false);

    const handleDialogOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleOnDismiss = useCallback(() => {
        if (onDismiss) onDismiss();
    }, [onDismiss]);

    return (
        <>
            <Dialog open={open} onClose={handleDialogClose}>
                <DialogTitle>
                    <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            width: "100%",
                        }}
                    >
                        <Typography
                            color={theme.palette.primary.textTitleColor}
                            variant="bodyLarge"
                        >
                            {t(
                                "views.home.components.tax_submissions_table_header.expiringIdentities.dialogTitle",
                            )}
                        </Typography>
                        <FontAwesomeIcon
                            icon={faXmark}
                            onClick={handleDialogClose}
                            style={{ cursor: "pointer" }}
                        />
                    </Stack>
                </DialogTitle>
                <Divider variant="fullWidth" />
                <DialogContent sx={{ width: "600px" }}>
                    <Stack flexDirection="row" gap={2}>
                        {variant === "expiredCredentials" ||
                        variant === "expiredSecurityEnvironments" ? (
                            <FontAwesomeIcon
                                icon={faCircleMinus}
                                color={theme.palette.primary.accentRustyRed}
                                size="xl"
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={faCircleExclamation}
                                color={theme.palette.primary.baseWarningColor}
                                size="xl"
                            />
                        )}
                        <Stack flexDirection="column" gap={1}>
                            <Typography>{t(DIALOG_TEXT[variant])}</Typography>
                            {identities.map((identity, index) => (
                                <Typography key={index} variant="bodyLarge700">
                                    {getIdentityText(identity)}
                                </Typography>
                            ))}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleDialogClose}>
                        {t(
                            "views.home.components.tax_submissions_table_header.expiringIdentities.dialog.back",
                        )}
                    </Button>
                    <Link
                        href="https://iampe.agenziaentrate.gov.it/sam/UI/Login?realm=/agenziaentrate"
                        target="_blank"
                        rel="noopener"
                        underline="none"
                        sx={{
                            display: "flex",
                            alignContent: "center",
                            height: "40px",
                            width: "fit-content",
                            border: `1px solid ${theme.palette.primary.cyanProgress}`,
                            background:
                                "linear-gradient(-26.56505117707799deg, rgb(0, 195, 234) 0%, rgb(0, 144, 209) 100%)",
                            py: 0.5,
                            px: 1,
                        }}
                    >
                        <Stack flexDirection="row" gap={1} alignItems="center">
                            <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                color="white"
                            />
                            <Typography color="white">
                                {t(
                                    "views.home.components.tax_submissions_table_header.expiringIdentities.dialog.entratel",
                                )}
                            </Typography>
                        </Stack>
                    </Link>
                </DialogActions>
            </Dialog>
            <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
            >
                {children}
                <Stack flexDirection="row" alignItems="center" gap={2}>
                    <Button variant="outlined" onClick={handleDialogOpen}>
                        {t(
                            "views.home.components.tax_submissions_table_header.expiringIdentities.show",
                        )}
                    </Button>
                    <Button onClick={handleOnDismiss}>
                        {t(
                            "views.home.components.tax_submissions_table_header.expiringIdentities.ignore",
                        )}
                    </Button>
                </Stack>
            </Stack>
        </>
    );
};
